import { FilterService } from "@primevue/core/api"
import dayjs from "dayjs"
import { type Component, defineAsyncComponent } from "vue"

import { getDateRangeFromRangeType } from "@/utils/date"

export type FilterType =
  | "select"
  | "statusSelect"
  | "string"
  | "multi_select"
  | "date"
  | "date_range"
  | "number"
  | "number_range"
  | "bool"
//| "time"
//| "check"
//| "color"

export const DEFAULT_COMPONENT_BY_FILTER_TYPE: {
  [key in FilterType]: Component
} = {
  string: defineAsyncComponent(() => import("@/components/common/BaseInput.vue")),
  select: defineAsyncComponent(() => import("@/components/common/BaseSelect.vue")),
  statusSelect: defineAsyncComponent(() => import("@/components/common/BaseSelect.vue")),
  multi_select: defineAsyncComponent(() => import("@/components/common/BaseMultiSelect.vue")),
  date: defineAsyncComponent(() => import("@/components/common/BaseDateInput.vue")),
  date_range: defineAsyncComponent(() => import("@/components/common/BaseDateRangeInput.vue")),
  number: defineAsyncComponent(() => import("@/components/common/BaseNumberInput.vue")),
  number_range: defineAsyncComponent(() => import("@/components/common/BaseNumberRangeInput.vue")),
  bool: defineAsyncComponent(() => import("@/components/common/BinarySelectButton.vue")),
  //time: () => import("@/components/common/BaseInputTime.vue"),
  //check: () => import("@/components/common/BaseCheckbox.vue"),
  //color: () => import("@/components/common/BaseColorSelector.vue"),
} as const

export const DEFAULT_MATCH_MODES_BY_FILTER_TYPE: { [key in FilterType]: FilterOption[] } = {
  string: ["contains"],
  select: ["equals"],
  statusSelect: ["equals"],
  multi_select: ["in"],
  date: ["dateIs"],
  date_range: [],
  number: ["equals"],
  number_range: [],
  bool: ["equals"],
  //time: ["DATE_IS"],
  //check: ["EQUALS"],
  //color: ["EQUALS"],
} as const

export const CustomFilters = {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  caseSensitiveEquals: (value: any, filter: any) => {
    if (filter === undefined || filter === null || filter.trim() === "") {
      return true
    }

    if (value === undefined || value === null) {
      return false
    }

    return value.toString() === filter.toString()
  },
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  caseSensitiveContains: (value: any, filter: any) => {
    if (!Array.isArray(value)) throw new Error("Wrong use of caseSensitiveContain!")
    if (filter === undefined || filter === null || filter.trim() === "") {
      return true
    }

    if (value === undefined || value === null) {
      return false
    }

    return value.includes(filter.toString())
  },
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  caseInsensitiveEquals: (value: any, filter: any) => {
    if (filter === undefined || filter === null || filter.trim() === "") {
      return true
    }

    if (value === undefined || value === null) {
      return false
    }

    return value.toString().toLowerCase() === filter.toString().toLowerCase()
  },
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  caseInsensitiveContains: (value: any, filter: any) => {
    if (!Array.isArray(value)) throw new Error("Wrong use of caseInsensitiveContains!")

    if (filter === undefined || filter === null || filter.trim() === "") {
      return true
    }

    if (value === undefined || value === null) {
      return false
    }

    const lowerFilter = filter.toString().toLowerCase()
    return value.some((v) => v?.toString().toLowerCase() === lowerFilter)
  },
  /**
   * Performs a case-insensitive, multi-word substring match.
   */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  caseInsensitiveMultiWordMatch: (value: any, filter: any) => {
    if (filter === undefined || filter === null || filter.trim() === "") {
      return true
    }
    if (value === undefined || value === null) {
      return false
    }

    if (typeof value !== "string" || typeof filter !== "string")
      throw Error("Wrong use of caseInsensitiveMultiWordMatch!")

    const target = value.toString().toLowerCase()
    const parts = filter.toString().toLowerCase().trim().split(/\s+/)

    return parts.every((p) => target.includes(p))
  },
  dateRangeContains: (value: Date[], filter: { from?: Date; to?: Date } | null) => {
    if (!value || !filter) return false

    const { from, to } = filter
    return value.some((date) => {
      if (from && to) return date >= from && date <= to
      else if (from) return date >= from
      else if (to) return date <= to
    })
  },
  // Select-based date range filtering using range type keys (today, this_week, ...)
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  typeBasedDateRange: (value: any, filter: any) => {
    if (!filter) return true
    const range = getDateRangeFromRangeType(filter)
    if (!range) return true
    const start = range.startDate.startOf("day")
    const end = range.endDate.endOf("day")
    return dayjs(value).isBetween(start, end, "day", "[]")
  },
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  sameDateOrAfter: (value: any, filter: any) => {
    if (!filter) return true
    const filterDay = dayjs(filter).startOf("day")
    return dayjs(value).isSameOrAfter(filterDay, "day")
  },
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  sameDateOrBefore: (value: any, filter: any) => {
    if (!filter || !value) return true
    const filterDay = dayjs(filter).endOf("day")
    return dayjs(value).isSameOrBefore(filterDay, "day")
  },
} as const

Object.entries(CustomFilters).forEach(([key, fn]) => {
  FilterService.register(key, fn)
})

export type FilterOption = keyof FilterService.filters | keyof typeof CustomFilters
